import React, { useState, useEffect, useRef } from 'react';
import { Col, Row } from '../../lib/utils';
import { BoldText, DemiLightText, RegularText } from '../../lib/Text';
import TabLockSettings from './TabLockSettings';
import SvgIcon from '../SvgIcon';
import { colors } from '../../lib/colors';
import { usePayrollAuthContext } from '../../context/PayrollAuthContext';
import { usePayrollAuth } from '../../hook/usePayrollAuth';

interface ModalTabLockSettingsProps {
    setIsVisible: (visible: boolean) => void;
}

const ModalTabLockSettings: React.FC<ModalTabLockSettingsProps> = ({ setIsVisible }) => {
    const { isUnlocked, unlockSection, setLastSection } = usePayrollAuthContext();
    const { isLoading, isPasswordSet, verifyPassword, setPassword } = usePayrollAuth();
    const [authenticated, setAuthenticated] = useState(isUnlocked);
    const [pw, setPw] = useState('');
    const [pw2, setPw2] = useState('');
    const [error, setError] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);

    const step = isPasswordSet ? 'confirm' : 'set';

    useEffect(() => {
        if (isUnlocked) setAuthenticated(true);
    }, [isUnlocked]);

    // Auto-focus password input
    useEffect(() => {
        if (!authenticated && !isLoading) {
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [authenticated, isLoading]);

    const handleSubmit = async () => {
        setError('');
        try {
            if (step === 'set') {
                if (pw !== pw2) {
                    setError('비밀번호가 일치하지 않습니다.');
                    return;
                }
                await setPassword(pw);
                unlockSection('tabLockSettings');
                setLastSection('tabLockSettings');
                setAuthenticated(true);
            } else {
                const result = await verifyPassword(pw);
                if (result.status === 200) {
                    unlockSection('tabLockSettings');
                    setLastSection('tabLockSettings');
                    setAuthenticated(true);
                }
            }
        } catch {
            setError('비밀번호가 올바르지 않습니다.');
            setPw('');
            setTimeout(() => inputRef.current?.focus(), 50);
        }
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') handleSubmit();
    };

    // Shared backdrop
    const backdrop = (children: React.ReactNode) => (
        <div
            style={{
                position: 'fixed',
                top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 9999,
            }}
            onClick={(e) => { if (e.target === e.currentTarget) setIsVisible(false); }}
        >
            {children}
        </div>
    );

    // Loading
    if (isLoading) return backdrop(<div />);

    // Not authenticated — inline auth form (same card style as settings)
    if (!authenticated) {
        return backdrop(
            <Col
                style={{
                    width: 480,
                    backgroundColor: 'white',
                    borderRadius: 20,
                    overflow: 'hidden',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
                }}
            >
                {/* Header */}
                <Row
                    style={{
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '20px 24px 16px',
                        borderBottom: '1px solid #F2F4F6',
                    }}
                >
                    <Row style={{ alignItems: 'center' }}>
                        <SvgIcon name="SvgLock" size={18} fill={colors.Text_1} stroke={colors.Text_1} style={{ marginRight: 8 }} />
                        <BoldText fontSize={18} lineHeight={26} color="#3D4244">
                            탭 잠금 설정
                        </BoldText>
                    </Row>
                    <div
                        onClick={() => setIsVisible(false)}
                        style={{ cursor: 'pointer', padding: '4px 8px', borderRadius: 6, fontSize: 18, color: colors.LAVEL_4, lineHeight: 1 }}
                        className="hover:bg-[#F2F4F6] transition-colors"
                    >
                        ✕
                    </div>
                </Row>

                {/* Auth body */}
                <Col style={{ padding: '24px' }}>
                    <DemiLightText fontSize={15} lineHeight={22} color="#3D4244" className="mb-[20px]">
                        {step === 'set' ? '2차 비밀번호를 설정해주세요' : '2차 비밀번호를 입력해주세요'}
                    </DemiLightText>

                    <input
                        ref={inputRef}
                        type="password"
                        value={pw}
                        onChange={(e) => setPw(e.target.value.replace(/[^0-9]/g, ''))}
                        onKeyDown={onKeyDown}
                        placeholder="비밀번호를 입력해주세요"
                        maxLength={20}
                        className="w-full h-[48px] px-[16px] border border-[#E0E4E8] rounded-[10px] text-[15px] outline-none focus:border-primary transition-colors mb-[12px]"
                    />

                    {step === 'set' && (
                        <input
                            type="password"
                            value={pw2}
                            onChange={(e) => setPw2(e.target.value.replace(/[^0-9]/g, ''))}
                            placeholder="비밀번호 확인을 위해 다시 입력해주세요"
                            maxLength={20}
                            className="w-full h-[48px] px-[16px] border border-[#E0E4E8] rounded-[10px] text-[15px] outline-none focus:border-primary transition-colors mb-[12px]"
                        />
                    )}

                    {error && (
                        <DemiLightText fontSize={13} lineHeight={18} color="#E53935" className="mb-[8px]">
                            {error}
                        </DemiLightText>
                    )}

                    <Row className="items-center mb-[16px]">
                        <SvgIcon name="SvgLock" size={12} fill={colors.LAVEL_4} stroke={colors.LAVEL_4} className="mr-[4px]" />
                        <DemiLightText fontSize={12} lineHeight={18} color="#8E9AA0">
                            인증 후 30분간 유지됩니다
                        </DemiLightText>
                    </Row>

                    <Row style={{ justifyContent: 'flex-end', gap: 8 }}>
                        <Col
                            className="w-[80px] h-[40px] bg-[#F2F4F6] rounded-[10px] cursor-pointer flex justify-center items-center hover:bg-[#E8EAED] transition-colors"
                            onClick={() => setIsVisible(false)}
                        >
                            <RegularText fontSize={14} lineHeight={20} color="#3D4244">취소</RegularText>
                        </Col>
                        <Col
                            className="w-[80px] h-[40px] bg-primary rounded-[10px] cursor-pointer flex justify-center items-center hover:opacity-90 transition-opacity"
                            onClick={handleSubmit}
                        >
                            <RegularText fontSize={14} lineHeight={20} color="white">확인</RegularText>
                        </Col>
                    </Row>
                </Col>
            </Col>
        );
    }

    // Authenticated — show settings
    return backdrop(
        <Col
            style={{
                width: 480,
                maxHeight: '80vh',
                backgroundColor: 'white',
                borderRadius: 20,
                overflow: 'hidden',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
            }}
        >
            {/* Header */}
            <Row
                style={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '20px 24px 16px',
                    borderBottom: '1px solid #F2F4F6',
                }}
            >
                <Row style={{ alignItems: 'center' }}>
                    <SvgIcon name="SvgLock" size={18} fill={colors.Text_1} stroke={colors.Text_1} style={{ marginRight: 8 }} />
                    <BoldText fontSize={18} lineHeight={26} color="#3D4244">
                        탭 잠금 설정
                    </BoldText>
                </Row>
                <div
                    onClick={() => setIsVisible(false)}
                    style={{ cursor: 'pointer', padding: '4px 8px', borderRadius: 6, fontSize: 18, color: colors.LAVEL_4, lineHeight: 1 }}
                    className="hover:bg-[#F2F4F6] transition-colors"
                >
                    ✕
                </div>
            </Row>

            {/* Body */}
            <div style={{ padding: '0 24px 24px', overflowY: 'auto' }}>
                <TabLockSettings isModal />
            </div>
        </Col>
    );
};

export default ModalTabLockSettings;
